import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "./shared";
import { PrivacyPolicyOverlay } from "@/components/PrivacyConsentCheckbox";
import {
    Shield, FileText, Upload, Eye, RotateCcw, Save, History, Download,
    ClipboardCheck, X, AlertTriangle, Search,
} from "lucide-react";

const POLICY_TYPE_LABEL = {
    default: { label: "Default VendiDirect", cls: "bg-ink-900 text-ink-300 border-white/15" },
    custom_text: { label: "Testo personalizzato", cls: "bg-purple-900/30 text-purple-200 border-purple-700/40" },
    custom_pdf: { label: "PDF personalizzato", cls: "bg-blue-900/30 text-blue-200 border-blue-700/40" },
};

export default function CrmPrivacy() {
    const [tab, setTab] = useState("policy");
    return (
        <div data-testid="crm-privacy" className="space-y-6">
            <div>
                <span className="label-eyebrow text-gold">VD Manager · GDPR</span>
                <h2 className="font-serif text-3xl text-ink-50 mt-2 flex items-center gap-2">
                    <Shield size={22} className="text-gold" /> Privacy Policy
                </h2>
                <p className="text-ink-400 text-sm mt-1">Gestisci il documento privacy della tua agenzia e consulta i consensi raccolti dai moduli di contatto.</p>
            </div>
            <div className="flex border border-white/15 w-fit">
                <TabPill id="policy" active={tab === "policy"} onClick={setTab}>Documento</TabPill>
                <TabPill id="consents" active={tab === "consents"} onClick={setTab}>Consensi ricevuti</TabPill>
            </div>
            {tab === "policy" && <PolicyEditor />}
            {tab === "consents" && <ConsentsList />}
        </div>
    );
}

function TabPill({ id, active, onClick, children }) {
    return (
        <button
            data-testid={`privacy-tab-${id}`}
            onClick={() => onClick(id)}
            className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${active ? "bg-gold text-ink-950" : "text-ink-300 hover:text-ink-50 hover:bg-white/5"}`}
        >
            {children}
        </button>
    );
}

// ============================================================
// TAB 1 · Editor policy
// ============================================================
function PolicyEditor() {
    const [current, setCurrent] = useState(null);
    const [versions, setVersions] = useState([]);
    const [mode, setMode] = useState("default"); // default | custom_text | custom_pdf
    const [customText, setCustomText] = useState("");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [preview, setPreview] = useState(false);
    const [uploadingPdf, setUploadingPdf] = useState(false);
    const [openVersion, setOpenVersion] = useState(null);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const [pol, hist] = await Promise.all([
                api.get("/privacy/policy/me"),
                api.get("/privacy/policy/me/versions"),
            ]);
            setCurrent(pol.data);
            setVersions(hist.data.items || []);
            setMode(pol.data.type);
            setCustomText(pol.data.type === "custom_text" ? (pol.data.content_text || "") : "");
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Errore caricamento");
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const savePolicy = async () => {
        if (mode === "custom_text" && !customText.trim()) {
            setMsg("Inserisci il testo della policy prima di salvare.");
            return;
        }
        setSaving(true); setMsg("");
        try {
            if (mode === "default") {
                await api.put("/privacy/policy/me", { type: "default" });
                setMsg("Policy ripristinata su default VendiDirect.");
            } else if (mode === "custom_text") {
                await api.put("/privacy/policy/me", { type: "custom_text", content_text: customText });
                setMsg("Policy personalizzata salvata.");
            }
            await load();
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Errore salvataggio");
        } finally { setSaving(false); }
    };

    const uploadPdf = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf")) {
            setMsg("Serve un file PDF"); return;
        }
        setUploadingPdf(true); setMsg("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post("/privacy/policy/me/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setMsg("PDF caricato correttamente.");
            await load();
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Errore upload PDF");
        } finally { setUploadingPdf(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    if (!current) return <div className="text-ink-400 text-sm py-12 text-center">Caricamento policy...</div>;
    const typeMeta = POLICY_TYPE_LABEL[current.type] || POLICY_TYPE_LABEL.default;

    return (
        <div className="space-y-6">
            {/* Stato attuale */}
            <div className="border border-white/10 bg-ink-900/40 p-5" data-testid="privacy-current-state">
                <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-gold">Documento attivo</div>
                        <div className="font-serif text-xl text-ink-50 mt-1">
                            {current.type === "default" ? "Privacy Policy Default VendiDirect" : `Privacy Policy — versione ${current.version}`}
                        </div>
                        <div className="text-[11px] text-ink-400 mt-1">
                            Ultimo aggiornamento: {current.updated_at ? fmtDate(current.updated_at) : "—"}
                            {current.updated_by_email ? ` · da ${current.updated_by_email}` : ""}
                        </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-3 py-1.5 border ${typeMeta.cls}`}>
                        {typeMeta.label}
                    </span>
                </div>
            </div>

            {/* Selettore modalità */}
            <div className="border border-white/10 bg-ink-900/40 p-5">
                <div className="text-[10px] uppercase tracking-wider text-gold mb-3">Sorgente policy</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <ModeCard
                        id="default"
                        active={mode === "default"}
                        onClick={() => setMode("default")}
                        title="Default VendiDirect"
                        desc="Template GDPR pre-scritto e sempre aggiornato con i tuoi dati di agenzia."
                    />
                    <ModeCard
                        id="custom_text"
                        active={mode === "custom_text"}
                        onClick={() => setMode("custom_text")}
                        title="Testo personalizzato"
                        desc="Scrivi o incolla il tuo testo. Ideale se lo tieni sincronizzato con il tuo studio legale."
                    />
                    <ModeCard
                        id="custom_pdf"
                        active={mode === "custom_pdf"}
                        onClick={() => setMode("custom_pdf")}
                        title="PDF personalizzato"
                        desc="Carica il PDF fornito dallo studio legale. Verrà mostrato ai clienti nell'overlay."
                    />
                </div>
            </div>

            {/* Editor */}
            {mode === "custom_text" && (
                <div className="border border-white/10 bg-ink-900/40 p-5">
                    <div className="text-[10px] uppercase tracking-wider text-gold mb-3">Testo della privacy policy</div>
                    <textarea
                        data-testid="privacy-custom-text"
                        rows={16}
                        value={customText}
                        onChange={(e) => setCustomText(e.target.value)}
                        placeholder="Incolla o scrivi qui il testo integrale della tua Privacy Policy..."
                        className="w-full bg-ink-950 border border-white/15 px-4 py-3 text-sm text-ink-100 focus:border-gold outline-none font-mono"
                    />
                    <div className="text-[10px] text-ink-500 mt-2">{customText.length.toLocaleString("it-IT")} caratteri</div>
                </div>
            )}

            {mode === "custom_pdf" && (
                <div className="border border-white/10 bg-ink-900/40 p-5">
                    <div className="text-[10px] uppercase tracking-wider text-gold mb-3">Carica PDF (max 10MB)</div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        data-testid="privacy-pdf-input"
                        onChange={(e) => uploadPdf(e.target.files?.[0])}
                        className="hidden"
                    />
                    <button
                        onClick={() => fileRef.current?.click()}
                        disabled={uploadingPdf}
                        data-testid="privacy-pdf-upload"
                        className="w-full border border-dashed border-white/20 hover:border-gold hover:bg-white/5 py-6 text-sm text-ink-200 flex items-center justify-center gap-2 transition-colors"
                    >
                        <Upload size={16} /> {uploadingPdf ? "Caricamento PDF..." : "Seleziona PDF"}
                    </button>
                    {current.type === "custom_pdf" && current.pdf_filename && (
                        <div className="mt-3 flex items-center gap-2 text-[12px] text-ink-300">
                            <FileText size={14} className="text-gold" /> PDF attivo: <span className="text-ink-100">{current?.pdf_filename}</span>
                            <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/privacy/policy/me/pdf`}
                                target="_blank" rel="noreferrer"
                                className="ml-auto cta-outline text-[10px] px-2 py-1 flex items-center gap-1"
                            >
                                <Eye size={11} /> Apri
                            </a>
                        </div>
                    )}
                </div>
            )}

            {msg && (
                <div className={`text-xs p-3 border ${msg.startsWith("Errore") || msg.startsWith("Serve") || msg.startsWith("Inserisci") ? "border-red-700/40 bg-red-900/20 text-red-300" : "border-gold/40 bg-gold/5 text-gold"}`} data-testid="privacy-msg">
                    {msg}
                </div>
            )}

            {/* Azioni */}
            <div className="flex flex-wrap gap-3 items-center">
                <button
                    onClick={() => setPreview(true)}
                    data-testid="privacy-preview-btn"
                    className="cta-outline px-4 py-2.5 text-xs flex items-center gap-2"
                >
                    <Eye size={13} /> Anteprima
                </button>
                {mode !== "custom_pdf" && (
                    <button
                        onClick={savePolicy}
                        disabled={saving}
                        data-testid="privacy-save-btn"
                        className="cta-gold px-5 py-2.5 text-xs flex items-center gap-2"
                    >
                        <Save size={13} /> {saving ? "Salvataggio..." : (mode === "default" ? "Usa default" : "Salva policy")}
                    </button>
                )}
            </div>

            {/* Storico versioni */}
            <div className="border border-white/10 bg-ink-900/40 p-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-[10px] uppercase tracking-wider text-gold flex items-center gap-2">
                        <History size={13} /> Storico versioni ({versions.length})
                    </div>
                </div>
                {versions.length === 0 ? (
                    <div className="text-xs text-ink-500 italic py-3">Nessuna versione precedente archiviata.</div>
                ) : (
                    <ul className="space-y-2">
                        {versions.map((v) => (
                            <li key={v.id} data-testid={`privacy-version-${v.id}`} className="flex items-center gap-3 border border-white/5 bg-ink-950/50 px-3 py-2">
                                <RotateCcw size={12} className="text-ink-400" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-ink-100">Versione {v.version} · <span className="text-ink-400">{POLICY_TYPE_LABEL[v.type]?.label || v.type}</span></div>
                                    <div className="text-[10px] text-ink-500">Sostituita il {fmtDate(v.replaced_at)}</div>
                                </div>
                                <button
                                    onClick={() => setOpenVersion(v.id)}
                                    className="text-[10px] cta-outline px-2 py-1 flex items-center gap-1"
                                    data-testid={`privacy-version-view-${v.id}`}
                                >
                                    <Eye size={11} /> Vedi
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {preview && (
                <PreviewOverlay
                    title="Anteprima Privacy Policy"
                    content={
                        mode === "custom_text"
                            ? customText
                            : (mode === "default"
                                ? (current.default_template_preview || current.content_text)
                                : null)
                    }
                    pdfUrl={mode === "custom_pdf" && current.type === "custom_pdf" ? `${process.env.REACT_APP_BACKEND_URL}/api/privacy/policy/me/pdf` : null}
                    onClose={() => setPreview(false)}
                />
            )}
            {openVersion && (
                <VersionOverlay versionId={openVersion} onClose={() => setOpenVersion(null)} />
            )}
        </div>
    );
}

function ModeCard({ id, active, onClick, title, desc }) {
    return (
        <button
            onClick={onClick}
            data-testid={`privacy-mode-${id}`}
            className={`text-left border p-4 transition-colors ${active ? "border-gold bg-gold/5" : "border-white/10 hover:border-white/25 bg-ink-950/50"}`}
        >
            <div className={`text-sm font-medium ${active ? "text-gold" : "text-ink-100"}`}>{title}</div>
            <div className="text-[11px] text-ink-400 mt-1">{desc}</div>
        </button>
    );
}

function PreviewOverlay({ title, content, pdfUrl, onClose }) {
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="privacy-preview-overlay">
            <div className="relative w-full max-w-3xl max-h-[85vh] border border-gold/30 bg-ink-950 flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                    <div className="font-serif text-lg text-ink-50 flex items-center gap-2">
                        <Eye size={15} className="text-gold" /> {title}
                    </div>
                    <button onClick={onClose} data-testid="privacy-preview-close" className="text-ink-400 hover:text-ink-50" aria-label="Chiudi"><X size={16} /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    {pdfUrl && (
                        <div className="h-[65vh]">
                            <iframe title="Preview PDF" src={pdfUrl} className="w-full h-full border border-white/10 bg-white" />
                        </div>
                    )}
                    {!pdfUrl && content && (
                        <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-ink-100">{content}</pre>
                    )}
                    {!pdfUrl && !content && (
                        <div className="text-ink-400 text-sm italic">Nessun contenuto da mostrare.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

function VersionOverlay({ versionId, onClose }) {
    const [v, setV] = useState(null);
    useEffect(() => {
        api.get(`/privacy/policy/me/versions/${versionId}`).then((r) => setV(r.data)).catch(() => onClose());
    }, [versionId, onClose]);
    if (!v) return null;
    const pdfUrl = v.type === "custom_pdf" ? `${process.env.REACT_APP_BACKEND_URL}/api/privacy/policy/me/versions/${versionId}/pdf` : null;
    return (
        <PreviewOverlay
            title={`Versione ${v.version} · archiviata ${fmtDate(v.replaced_at)}`}
            content={v.content_text}
            pdfUrl={pdfUrl}
            onClose={onClose}
        />
    );
}

// ============================================================
// TAB 2 · Consensi ricevuti
// ============================================================
function ConsentsList() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [df, setDf] = useState("");
    const [dt, setDt] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (df) params.date_from = df;
            if (dt) params.date_to = dt;
            const r = await api.get("/privacy/consents/me", { params });
            setItems(r.data.items || []);
            setTotal(r.data.total_all_time || 0);
        } catch { setItems([]); }
        finally { setLoading(false); }
    }, [q, df, dt]);
    useEffect(() => { load(); }, [load]);

    const csvUrl = useMemo(() => {
        const p = new URLSearchParams();
        if (df) p.set("date_from", df);
        if (dt) p.set("date_to", dt);
        if (q) p.set("q", q);
        const qs = p.toString();
        return `${process.env.REACT_APP_BACKEND_URL}/api/privacy/consents/me/export.csv${qs ? "?" + qs : ""}`;
    }, [df, dt, q]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Consensi totali" value={total} testid="consent-total" />
                <StatCard label="Filtrati" value={items.length} testid="consent-filtered" />
                <StatCard
                    label="Ultimo consenso"
                    value={items[0]?.created_at ? fmtDate(items[0].created_at) : "—"}
                    small
                    testid="consent-last"
                />
                <StatCard label="Range attivo" value={df || dt ? `${df || "…"} → ${dt || "…"}` : "Tutti"} small testid="consent-range" />
            </div>

            <div className="flex flex-wrap gap-2 items-center border border-white/10 bg-ink-900/40 p-4">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                    <input
                        data-testid="consent-search"
                        placeholder="Cerca per nome o email..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="w-full bg-transparent border border-white/15 pl-9 pr-3 py-2 text-sm text-ink-100 outline-none focus:border-gold"
                    />
                </div>
                <label className="text-[10px] uppercase tracking-wider text-ink-400 flex items-center gap-2">
                    Dal
                    <input data-testid="consent-date-from" type="date" value={df} onChange={(e) => setDf(e.target.value)} className="bg-ink-950 border border-white/15 px-2 py-1.5 text-sm text-ink-100 focus:border-gold outline-none" />
                </label>
                <label className="text-[10px] uppercase tracking-wider text-ink-400 flex items-center gap-2">
                    Al
                    <input data-testid="consent-date-to" type="date" value={dt} onChange={(e) => setDt(e.target.value)} className="bg-ink-950 border border-white/15 px-2 py-1.5 text-sm text-ink-100 focus:border-gold outline-none" />
                </label>
                <a
                    href={csvUrl}
                    data-testid="consent-export-csv"
                    className="cta-gold px-4 py-2 text-[11px] flex items-center gap-2"
                >
                    <Download size={12} /> Esporta CSV
                </a>
            </div>

            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink-950 text-[10px] uppercase tracking-wider text-ink-400">
                        <tr>
                            <th className="px-3 py-2.5 text-left">Data</th>
                            <th className="px-3 py-2.5 text-left">Nome</th>
                            <th className="px-3 py-2.5 text-left">Email</th>
                            <th className="px-3 py-2.5 text-left">IP</th>
                            <th className="px-3 py-2.5 text-left">Policy</th>
                            <th className="px-3 py-2.5 text-left">Fonte</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={6} className="text-center text-ink-400 py-8 text-xs">Caricamento...</td></tr>
                        )}
                        {!loading && items.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-ink-500 py-8 text-xs italic">Nessun consenso raccolto in questo intervallo.</td></tr>
                        )}
                        {items.map((c) => (
                            <tr key={c.id} data-testid={`consent-row-${c.id}`} className="border-t border-white/5 hover:bg-white/5">
                                <td className="px-3 py-2.5 text-ink-300">{fmtDate(c.created_at)}</td>
                                <td className="px-3 py-2.5 text-ink-100">{c.contact_name || "—"}</td>
                                <td className="px-3 py-2.5 text-ink-300 truncate">{c.contact_email || "—"}</td>
                                <td className="px-3 py-2.5 text-ink-400 font-mono text-[11px]">{c.ip || "—"}</td>
                                <td className="px-3 py-2.5">
                                    <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 border ${POLICY_TYPE_LABEL[c.policy_type]?.cls || POLICY_TYPE_LABEL.default.cls}`}>
                                        {POLICY_TYPE_LABEL[c.policy_type]?.label || c.policy_type} · {c.policy_version}
                                    </span>
                                </td>
                                <td className="px-3 py-2.5 text-ink-400 text-[11px]">{c.source || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatCard({ label, value, small, testid }) {
    return (
        <div className="border border-white/10 bg-ink-900/40 p-4" data-testid={testid}>
            <div className={`text-ink-50 ${small ? "text-sm font-medium" : "font-serif text-2xl"}`}>{value}</div>
            <div className="text-[10px] text-ink-400 mt-1 uppercase tracking-wider">{label}</div>
        </div>
    );
}
